import { Link } from 'react-router-dom';
import { Star, Gem, Crown, Medal, Award } from 'lucide-react';
import { compact } from '../lib/api';
import { ARENA, rarityOf } from '../lib/classTheme';

/* ---------- AVATAR ---------- */
export function Avatar({ src, name, size = 40, ring, square, gold }) {
  const initial = (name || '?')[0].toUpperCase();
  return (
    <div className={`relative overflow-hidden grid place-items-center text-white font-black shrink-0
      ${square ? 'rounded-xl' : 'rounded-full'}`}
      style={{
        width: size, height: size, fontSize: size * 0.4,
        background: ARENA.goldFrame,
        padding: gold ? 2 : 0,
        boxShadow: ring ? `0 0 0 2px ${ARENA.gold}, 0 0 0 4px var(--marble)` : undefined,
      }}>
      <div className={`w-full h-full overflow-hidden grid place-items-center
        ${square ? 'rounded-[10px]' : 'rounded-full'}`}
        style={{ background: src ? 'transparent' : 'linear-gradient(135deg,#dcb254,#a87a2b)' }}>
        {src ? <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" /> : initial}
      </div>
    </div>
  );
}

/* ---------- HERO PORTRAIT ---------- */
export function HeroPortrait({ hero, size = 'md', showLevel = true, glow }) {
  const s = { sm: 'w-14 h-20', md: 'w-20 h-28', lg: 'w-28 h-40', xl: 'w-36 h-52' }[size];
  return (
    <div className="relative shrink-0">
      <div className={`${s} rounded-xl overflow-hidden relative`}
        style={{
          padding: 2, background: ARENA.goldFrame,
          boxShadow: glow ? `0 0 22px -4px ${glow}` : '0 4px 14px -4px rgba(36,30,22,.4)',
        }}>
        <div className="w-full h-full rounded-[10px] overflow-hidden"
          style={{ background: 'var(--stone-200)' }}>
          {hero?.image_url || hero?.image
            ? <img src={hero.image_url || hero.image} alt={hero.name}
                className="w-full h-full object-cover object-top" />
            : <div className="w-full h-full grid place-items-center text-2xl opacity-40">⚔️</div>}
        </div>
      </div>
      {showLevel && hero?.level && (
        <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full
          text-[9px] font-display font-black text-white"
          style={{ background: ARENA.goldFrame, boxShadow: '0 2px 8px -2px rgba(133,96,31,.6)' }}>
          {hero.level}
        </span>
      )}
    </div>
  );
}

/* ---------- STARS ---------- */
export function Stars({ value = 0, count, size = 14, interactive, onChange }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} type="button" disabled={!interactive}
          onClick={() => onChange?.(i)}
          className={interactive ? 'active:scale-90 transition' : 'cursor-default'}>
          <Star size={size}
            style={{
              fill: i <= Math.round(value) ? ARENA.gold : 'var(--stone-200)',
              color: i <= Math.round(value) ? ARENA.gold : 'var(--stone-300)',
            }} />
        </button>
      ))}
      {count !== undefined && (
        <span className="text-xs font-semibold ml-1" style={{ color: 'var(--stone-400)' }}>
          ({count})
        </span>
      )}
    </div>
  );
}

/* ---------- POINTS ---------- */
export function Points({ value, size = 'md' }) {
  const c = size === 'sm' ? 'text-[11px] px-2 py-0.5' : 'text-sm px-2.5 py-1';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-black text-white ${c}`}
      style={{ background: ARENA.goldFrame, boxShadow: '0 2px 8px -2px rgba(201,151,58,.5)' }}>
      <Gem size={size === 'sm' ? 10 : 12} /> {compact(value)}
    </span>
  );
}

/* ---------- RANK BADGE ---------- */
export function RankBadge({ rank, size = 32 }) {
  const styles = {
    1: { bg: 'linear-gradient(135deg,#e8c874,#c9973a)', Icon: Crown, glow: 'rgba(201,151,58,.6)' },
    2: { bg: 'linear-gradient(135deg,#e8dcc8,#b8a082)', Icon: Medal, glow: 'rgba(184,160,130,.5)' },
    3: { bg: 'linear-gradient(135deg,#d6a06a,#a87a2b)', Icon: Award, glow: 'rgba(168,122,43,.5)' },
  }[rank];

  if (!styles) {
    return (
      <div className="grid place-items-center font-display font-black text-xs rounded-xl shrink-0"
        style={{
          width: size, height: size,
          background: 'var(--stone-100)', color: 'var(--stone-500)',
          border: '1px solid var(--stone-200)',
        }}>
        {rank}
      </div>
    );
  }

  const { bg, Icon, glow } = styles;
  return (
    <div className="grid place-items-center text-white rounded-xl shrink-0"
      style={{ width: size, height: size, background: bg, boxShadow: `0 3px 12px -3px ${glow}` }}>
      <Icon size={size * 0.5} />
    </div>
  );
}

/* ---------- TIER BADGE ---------- */
const TIER_STYLE = {
  1: { bg: 'var(--stone-200)', color: 'var(--stone-600)' },
  2: { bg: 'rgba(52,211,153,.18)', color: '#047857' },
  3: { bg: 'rgba(59,130,246,.16)', color: '#1d4ed8' },
  4: { bg: 'rgba(168,85,247,.18)', color: '#7c3aed' },
  5: { bg: 'rgba(201,151,58,.22)', color: '#85601f' },
  6: { bg: ARENA.goldFrame, color: '#fff' },
};

export function TierBadge({ tier, label, multiplier }) {
  const s = TIER_STYLE[tier] || TIER_STYLE[1];
  return (
    <span className="chip font-display" style={{ background: s.bg, color: s.color }}>
      T{tier} {label} {multiplier && `· ${Number(multiplier).toFixed(2)}×`}
    </span>
  );
}

/* ---------- EMPTY ---------- */
export function Empty({ icon: Icon, title, sub, action, emoji }) {
  return (
    <div className="card p-8 text-center">
      {emoji ? <div className="text-4xl mb-2">{emoji}</div>
        : Icon ? <Icon size={36} className="mx-auto mb-3" style={{ color: 'var(--stone-300)' }} /> : null}
      <p className="font-display font-bold" style={{ color: 'var(--stone-700)' }}>{title}</p>
      {sub && <p className="text-sm mt-1 max-w-xs mx-auto" style={{ color: 'var(--stone-500)' }}>{sub}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/* ---------- SECTION HEAD ---------- */
export function SectionHead({ title, sub, right }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div className="min-w-0">
        <h2 className="font-display text-lg font-extrabold text-engraved tracking-wide"
          style={{ color: 'var(--stone-800)' }}>{title}</h2>
        {sub && <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-500)' }}>{sub}</p>}
      </div>
      {right}
    </div>
  );
}

/* ---------- SKELETON ---------- */
export function SkeletonList({ n = 4, h = 'h-24' }) {
  return <div className="space-y-3">{Array.from({ length: n }).map((_, i) =>
    <div key={i} className={`skeleton ${h}`} />)}</div>;
}

/* ---------- SHEET ---------- */
export function Sheet({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 animate-fade-up"
        style={{ background: 'rgba(36,30,22,.55)', backdropFilter: 'blur(4px)' }}
        onClick={onClose} />
      <div className={`relative w-full ${wide ? 'sm:max-w-2xl' : 'sm:max-w-md'}
        rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto animate-fade-up`}
        style={{
          background: ARENA.marbleBg,
          border: '1px solid var(--stone-200)',
          boxShadow: '0 -8px 40px -12px rgba(36,30,22,.35)',
        }}>
        <div className="sticky top-0 z-10 px-5 py-4 flex items-center justify-between rounded-t-3xl"
          style={{
            background: 'rgba(251,250,247,.95)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid var(--stone-200)',
          }}>
          <h3 className="font-display font-extrabold text-lg tracking-wide"
            style={{ color: 'var(--stone-800)' }}>{title}</h3>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg font-bold transition"
            style={{ background: 'var(--stone-100)', color: 'var(--stone-500)' }}>✕</button>
        </div>
        <div className="p-5 safe-bottom">{children}</div>
      </div>
    </div>
  );
}

/* ---------- SHOP CARD ---------- */
export function ShopCard({ shop }) {
  return (
    <Link to={`/shop/${shop.slug}`} className="card-h p-3 flex gap-3 items-center">
      <Avatar src={shop.logo_url} name={shop.name} size={48} square gold />
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate">{shop.name}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <Stars value={shop.rating_avg} count={shop.rating_count} size={11} />
          <span className="chip-ink">{shop.kind.replace('_', ' ')}</span>
          {shop.city_handle && <span className="chip-brand">{shop.city_handle}</span>}
        </div>
        {shop.community !== undefined && (
          <p className="text-[11px] font-semibold mt-1" style={{ color: 'var(--stone-400)' }}>
            👥 {compact(shop.community)} customers
          </p>
        )}
      </div>
    </Link>
  );
}